<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { usePlanningApi } from '../composables/usePlanningApi'
import type { ProductionOrder } from '../types/planning'

/**
 * Production calendar on real order dates: every start and end date that exists
 * on the loaded orders, grouped per day.
 */

const { t } = useI18n()
const { listProductionOrders } = usePlanningApi()

const orders = ref<ProductionOrder[]>([])
const loading = ref(true)
const error = ref('')
const month = ref(new Date().toISOString().slice(0, 7))
const status = ref('')

const statuses = ['', 'planned', 'in_progress', 'completed', 'cancelled']

const days = computed(() => {
  const grouped = new Map<string, { starts: ProductionOrder[]; finishes: ProductionOrder[] }>()
  for (const order of orders.value) {
    for (const [date, kind] of [
      [order.start_date, 'starts'] as const,
      [order.end_date, 'finishes'] as const,
    ]) {
      if (!date) continue
      const entry = grouped.get(date) ?? { starts: [], finishes: [] }
      entry[kind].push(order)
      grouped.set(date, entry)
    }
  }
  return [...grouped.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, entry]) => ({ date, ...entry }))
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listProductionOrders({
      status: status.value || undefined,
      page: 1,
      per_page: 100,
    })
    orders.value = result.items
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

function statusClass(value: string): string {
  if (value === 'completed') return 'status-ok'
  if (value === 'cancelled') return 'status-critical'
  if (value === 'in_progress') return 'status-info'
  return 'status-neutral'
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('planning.eyebrow') }}</p>
      <h2>{{ t('planning.calendar.title') }}</h2>
      <p>{{ t('planning.calendar.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <select v-model="status" class="filter-select" @change="load">
      <option v-for="value in statuses" :key="value || 'all'" :value="value">
        {{ value ? t(`planning.orderStatus.${value}`) : t('planning.common.allStatuses') }}
      </option>
    </select>
    <span class="spacer" />
    <div class="summary">{{ t('planning.common.rows', { count: orders.length }) }}</div>
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('planning.common.refresh') }}
    </button>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('planning.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('planning.common.retry') }}</button>
  </div>

  <div v-else-if="days.length === 0" class="state-message">
    <p>{{ t('planning.calendar.noOrders') }}</p>
  </div>

  <section v-else class="calendar-grid">
    <article v-for="day in days" :key="day.date" class="calendar-day">
      <h3>{{ formatDisplayDate(day.date) }}</h3>
      <div class="calendar-events">
        <div
          v-for="order in [...day.starts, ...day.finishes]"
          :key="`${day.date}-${order.id}`"
          class="calendar-event"
        >
          <strong>{{ order.mo_code }}</strong>
          <span>
            {{ day.starts.includes(order) ? t('planning.calendar.start') : t('planning.calendar.finish') }} ·
            {{ order.department_name || '—' }}
          </span>
          <small>{{ order.product_name || '—' }}</small>
          <span class="status-badge" :class="statusClass(order.status)">
            {{ t(`planning.orderStatus.${order.status}`) }}
          </span>
        </div>
      </div>
    </article>
  </section>
</template>
